import { computed, inject, Injectable, signal } from '@angular/core';
import { PaymentMethod, PosOrder } from '@klubovna/api-client';
import { RECENT_ORDERS_CONFIG } from '@posConstants';
import { RecentOrder, RecentOrderItem } from '@posModels';
import { PosOrdersApiService } from '@posServices';

@Injectable({
  providedIn: 'root',
})
export class RecentOrdersService {
  private ordersApi = inject(PosOrdersApiService);
  private orders = signal<RecentOrder[]>([]);
  private loadedSessionId = signal<string | null>(null);
  private currentPage = signal(1);
  private totalPages = signal(0);
  private visibleCount = signal<number>(RECENT_ORDERS_CONFIG.MAX_RECENT_ORDERS);

  readonly recentOrders = computed(() => this.orders().slice(0, this.visibleCount()));
  readonly hasOrders = computed(() => this.orders().length > 0);
  readonly hasMore = computed(() =>
    this.visibleCount() < this.orders().length || this.currentPage() < this.totalPages(),
  );
  readonly loading = signal(false);

  addOrder(order: PosOrder, metadata?: { tabName?: string; paymentMethod?: PaymentMethod }): void {
    const items: RecentOrderItem[] = (order.items ?? []).map(item => ({
      orderId: order.id,
      productName: item.nameSnapshot,
      qty: item.qty,
      priceCents: item.lineTotalCentsSnapshot,
      pricePoints: item.lineTotalPointsSnapshot,
      paymentType: item.paymentType,
    }));

    const recentOrder: RecentOrder = {
      id: order.id,
      createdAt: order.createdAt,
      items,
      totalCents: order.totalCentsSnapshot,
      totalPoints: order.totalPointsSnapshot,
      isOverhead: order.isOverhead,
      isTab: !!order.tabId,
      tabName: metadata?.tabName,
      paymentMethod: metadata?.paymentMethod,
      cashierName: order.createdBy ? `${ order.createdBy.firstName } ${ order.createdBy.lastName }` : undefined,
    };

    this.orders.update(current => [ recentOrder, ...current ]);
  }

  loadFromSession(sessionId: string): void {
    if (this.loadedSessionId() === sessionId || this.loading()) {
      return;
    }

    this.loadedSessionId.set(sessionId);
    this.currentPage.set(1);
    this.loading.set(true);
    this.ordersApi.getBySession(sessionId, RECENT_ORDERS_CONFIG.MAX_RECENT_ORDERS, 1).subscribe({
      next: (response) => {
        this.orders.set(response.items.map(order => this.mapPosOrderToRecentOrder(order)));
        this.totalPages.set(response.totalPages);
        this.loading.set(false);
      },
      error: () => {
        this.loadedSessionId.set(null);
        this.loading.set(false);
      },
    });
  }

  loadMore(): void {
    if (this.loading() || !this.hasMore()) {
      return;
    }

    this.visibleCount.update(count => count + RECENT_ORDERS_CONFIG.MAX_RECENT_ORDERS);

    const sessionId = this.loadedSessionId();
    const hasMorePages = this.currentPage() < this.totalPages();
    if (!sessionId || !hasMorePages) {
      return;
    }

    const nextPage = this.currentPage() + 1;
    this.loading.set(true);
    this.ordersApi.getBySession(sessionId, RECENT_ORDERS_CONFIG.MAX_RECENT_ORDERS, nextPage).subscribe({
      next: (response) => {
        const newOrders = response.items.map(order => this.mapPosOrderToRecentOrder(order));
        this.orders.update(current => {
          const existingIds = new Set(current.map(o => o.id));
          return [ ...current, ...newOrders.filter(o => !existingIds.has(o.id)) ];
        });
        this.currentPage.set(nextPage);
        this.totalPages.set(response.totalPages);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private mapPosOrderToRecentOrder(order: PosOrder): RecentOrder {
    const items: RecentOrderItem[] = (order.items ?? []).map(item => ({
      orderId: order.id,
      productName: item.nameSnapshot,
      qty: item.qty,
      priceCents: item.lineTotalCentsSnapshot,
      pricePoints: item.lineTotalPointsSnapshot,
      paymentType: item.paymentType,
    }));

    return {
      id: order.id,
      createdAt: order.createdAt,
      items,
      totalCents: order.totalCentsSnapshot,
      totalPoints: order.totalPointsSnapshot,
      isOverhead: order.isOverhead,
      isTab: !!order.tabId,
      tabName: order.tab?.visitorAlias ?? order.tab?.user?.firstName,
      paymentMethod: order.paymentMethod,
      cashierName: order.createdBy ? `${ order.createdBy.firstName } ${ order.createdBy.lastName }` : undefined,
    };
  }

  clear(): void {
    this.orders.set([]);
    this.loadedSessionId.set(null);
    this.currentPage.set(1);
    this.totalPages.set(0);
    this.visibleCount.set(RECENT_ORDERS_CONFIG.MAX_RECENT_ORDERS);
  }
}
